import { useQuery } from '@tanstack/react-query';
import type { Role } from '@cfl/shared';

import { api } from './api';

export interface PersonRow {
  id: string;
  displayName: string;
  /** Short name, if they have one. Shown in team lists people recognise. */
  alias: string | null;
  email: string;
  jobTitle: string | null;
  accountNumber: string | null;
  timezone: string;
  role: Role;
  enabled: boolean;
  avatarUrl: string | null;
  /** Null means they have never signed in to this system at all. */
  lastLoginAt: string | null;
  lastLoginMethod: 'GOOGLE' | 'PASSWORD' | 'DEV' | null;
  supervisor: { id: string; displayName: string } | null;
  teams: { id: string; name: string; isLead: boolean }[];
  /**
   * The person's own default rates, in minor units, as strings.
   *
   * The last fallback in the rate chain - an entry's own rate, then the activity,
   * project and customer rates, then these. Only sent to HR and above, which is
   * everybody who can reach /people at all.
   */
  hourlyRateMinor: string | null;
  internalRateMinor: string | null;
}

export interface TeamRow {
  id: string;
  name: string;
  memberCount: number;
  leads: { id: string; displayName: string }[];
  /** Full membership, for the member editor. */
  members?: { id: string; displayName: string; isLead: boolean }[];
}

export const usePeople = (includeDisabled: boolean) =>
  useQuery({
    queryKey: ['people', { includeDisabled }],
    queryFn: () => api.get<PersonRow[]>(`/people?includeDisabled=${includeDisabled}`),
  });

/** Just enough to put somebody in a picker. */
export interface PersonOption {
  id: string;
  displayName: string;
  alias: string | null;
  jobTitle: string | null;
}

/**
 * The people this viewer may see, for pickers.
 *
 * Not `usePeople`: that reads /people, which is HR-only because it carries rates,
 * staff numbers and sign-in history. Every picker outside Administration was
 * calling it as a supervisor, getting a 403, and rendering an empty list without
 * saying anything.
 */
export const useVisiblePeople = (enabled = true) =>
  useQuery({
    queryKey: ['people', 'visible'],
    queryFn: () => api.get<PersonOption[]>('/reporting/people'),
    enabled,
  });

export const useTeams = () =>
  useQuery({ queryKey: ['people', 'teams'], queryFn: () => api.get<TeamRow[]>('/people/teams') });
